import { ReadonlyVec3 } from 'gl-matrix';
import {
    syncCanvasSizeToLayout,
    tracePolyline,
} from '~/engine/core/render/overlayCanvas';
import { PlaneView } from '~/engine/view/views/PlaneView';

/** One uncommitted loop, already in PATIENT (world) mm. `color` is
 *  [0,1] floats. */
export interface GhostPreviewPolygon {
    points: ReadonlyArray<ReadonlyVec3>;
    color: ReadonlyVec3;
}

export type GhostPreviewState = {
    polygons: ReadonlyArray<GhostPreviewPolygon>;
};

/**
 * Renders dashed "ghost" polygons — an uncommitted preview of geometry
 * nothing has written yet — onto the 2D overlay canvas of the provided
 * {@link PlaneView}. The ONE dashed-preview implementation: contour
 * auto-suggestion adapts its voxel-space marching-squares output onto
 * this (see `ContourAutoSuggestionPreviewRenderer`), and the couch-model
 * preview feeds it world-space loops directly — a ghost-style fix lands
 * in both at once instead of drifting apart across copies.
 *
 * The renderer owns no business logic; it is driven entirely by
 * {@link setState} / {@link clear} calls from the caller. The 2D overlay
 * sits above the WebGL viewport, so re-rendering on every view-state
 * change (slice scroll, pan, zoom) is the caller's responsibility.
 */
export class DashedGhostPreviewRenderer {
    private static readonly DASH_PATTERN = [6, 4];
    private static readonly LINE_WIDTH = 1.5;

    private readonly planeView: PlaneView;
    private readonly canvasContext: CanvasRenderingContext2D;
    private current: GhostPreviewState | undefined;

    public constructor(planeView: PlaneView) {
        this.planeView = planeView;
        const canvas = this.planeView.get2DCanvas;
        if (!canvas) {
            throw new Error(
                'DashedGhostPreviewRenderer: 2D canvas unavailable'
            );
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error(
                'DashedGhostPreviewRenderer: 2D context unavailable'
            );
        }
        this.canvasContext = ctx;
    }

    public setState(state: GhostPreviewState): void {
        this.current = state;
        this.render();
    }

    /** Re-render with the current state (e.g. after a pan/zoom event). */
    public refresh(): void {
        if (this.current) {
            this.render();
        }
    }

    public clear(): void {
        this.current = undefined;
        const ctx = this.canvasContext;
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    }

    public dispose(): void {
        this.clear();
    }

    private render(): void {
        const ctx = this.canvasContext;
        if (!this.current) {
            return;
        }

        syncCanvasSizeToLayout(ctx);
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        ctx.lineWidth = DashedGhostPreviewRenderer.LINE_WIDTH;
        ctx.setLineDash(DashedGhostPreviewRenderer.DASH_PATTERN);

        for (const polygon of this.current.polygons) {
            if (polygon.points.length < 2) {
                continue;
            }
            const R = Math.round(polygon.color[0] * 255);
            const G = Math.round(polygon.color[1] * 255);
            const B = Math.round(polygon.color[2] * 255);
            ctx.fillStyle = `rgba(${R}, ${G}, ${B}, 0.3)`;
            ctx.strokeStyle = `rgba(${R}, ${G}, ${B}, 0.8)`;

            ctx.beginPath();
            tracePolyline(ctx, this.planeView, polygon.points);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
        }

        ctx.setLineDash([]);
    }
}
